import React, { useEffect } from 'react';
import {
	change,
	Field,
	getFormMeta,
	getFormSyncErrors,
	getFormAsyncErrors,
	getFormValues,
	initialize
} from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import  {
	Modal,
	Button,
	ProgressIndicator,
	ReduxControls
} from "rhombus-react";

import useAdvanceWizard from 'hooks/useAdvanceWizard';

import { FORMAT_SELECT_INDEX } from "./constants";
import Breadcrumb from './Breadcrumb';
import ConceptLoadError from './ConceptLoadError';

import SocialAdTypeSelector from 'social/SocialAdTypeSelector';
import { INTIIALIZERS_BY_AD_TYPE, defaultInitializer } from 'social/initializers';

import { SOCIAL_WIZARD } from 'widgets/forms/list';
import { required } from 'widgets/forms/validators/validators';
import { conceptNameAllowedChars, conceptNameMaxLength, CONCEPT_NAME_MAX_LENGTH } from 'widgets/forms/validators/Concepts';

import "./FormatSelectModal.css";

export default function FormatSelectModal(props){
	const {
		asyncValidating,
		onClose,
		onNext,
	} = props;
	const dispatch = useDispatch();

	const { values = {}, meta = {} } = useSelector(state => ({
		values: getFormValues(SOCIAL_WIZARD)(state),
		meta: getFormMeta(SOCIAL_WIZARD)(state),
	}));

	const { adTypes, conceptId } = values;
	const isAsyncValidating = (asyncValidating === "conceptName");

	const isDSPLoaded = useSelector(state => state.social?.isLoaded);
	const conceptDetails = useSelector(state => state.conceptDetails);
	const isConceptLoaded = conceptDetails?.isLoaded;
	const isLoading = (conceptId && !isConceptLoaded) || !isDSPLoaded;

	const existingConceptName = conceptId && conceptDetails?.data?.conceptName;
	const existingAdTypes = (conceptId && conceptDetails?.data?.adTypes) || [];
	const hasConceptLoadError = conceptId && conceptDetails?.hasErrorLoading;

	const title = conceptId ? `Add Social Creative to Concept - ${existingConceptName}` : "Social Concept";

	const [attemptAdvance, isAttemptingAdvance] = useAdvanceWizard(SOCIAL_WIZARD, onNext);

	useEffect(() => {
		if(existingConceptName){
			// We set initial values here so as to not mark the form dirty
			dispatch(initialize(SOCIAL_WIZARD, {
				conceptId,
				conceptName: existingConceptName,
			}));
		}
	}, [existingConceptName]);

	const canAdvance = useSelector((state) => {
		const syncError = getFormSyncErrors(SOCIAL_WIZARD)(state)?.conceptName;
		const asyncError = getFormAsyncErrors(SOCIAL_WIZARD)(state)?.conceptName;
		return (!isAttemptingAdvance && adTypes?.length && !syncError && !asyncError);
	});

	/* When we get a new concept name, we need to update default values for untouched creative names */
	function handleCreativeNameChange(newCreativeName) {
		if (!values.adTypes) { return; }

		values.adTypes.forEach((adType) => {
			const initializer = INTIIALIZERS_BY_AD_TYPE[adType] || defaultInitializer(adType);

			if (!initializer) { return; }

			const newValues = initializer({ ...values, conceptName: newCreativeName }, meta);

			dispatch(change(SOCIAL_WIZARD, adType, newValues));
		});
	}

	/* When we select different adTypes, we need to update default values for untouched creative names */
	function handleSelect(newAdTypes) {
		if (!Array.isArray(newAdTypes)) { return; }

		newAdTypes.forEach((adType) => {
			const initializer = INTIIALIZERS_BY_AD_TYPE[adType] || defaultInitializer(adType);

			if (!initializer) { return; }

			const newValues = initializer(values, meta);

			dispatch(change(SOCIAL_WIZARD, adType, newValues));
		});
	}

	const $fields = (
		<>
			{existingConceptName ? null : (<Field
				component={ReduxControls.Input}
				name="conceptName"
				label="Concept Name"
				className="concept-name"
				validate={[required, conceptNameAllowedChars, conceptNameMaxLength]}
				onChange={handleCreativeNameChange}
				charMax={CONCEPT_NAME_MAX_LENGTH}
				showCharCount
				required
				autoFocus = {!values.conceptName}
			/>)}
			<Field
				name="adTypes"
				component={SocialAdTypeSelector}
				disabledAdTypes = {existingAdTypes}
				onChange={handleSelect}
			/>
		</>
	);
	const $error = <ConceptLoadError />;
	const $content = hasConceptLoadError ? $error : $fields;

	function renderContent(){
		return (
			<div className="content">
				<Breadcrumb currentStep={FORMAT_SELECT_INDEX} />
				<div className="format-select-content">
					<ProgressIndicator isLoading={isLoading} />
					{$content}
				</div>
			</div>
		);
	}

	function renderFooter(){
		return (
			<>
				<Button theme="default" onClick={onClose}>Cancel</Button>
				<Button
					theme = "blue"
					onClick={attemptAdvance}
					disabled = {!canAdvance}
					icon = {isAsyncValidating ? "fas fa-spinner circle-spin" : null}
				>
					Next to Add Content
				</Button>
			</>
		);
	}

	let modalProps = {
		title,
		setAppTitle: false,
		type: "modal",
		className: "social-modal white-title-bar format-select",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		renderContent,
		renderFooter,
		onClose,
	};

	return <Modal {...modalProps} />;
}
